import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useToken } from '../../../hooks/useToken';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import { Visibility, Close as CloseIcon } from '@mui/icons-material';
import { getClosedTicketsByBoard, clearDateFilters } from '../../../reducers/kanban/ticketActions';
import TicketDetailModal from './TicketDetailModal';

/**
 * Modal para mostrar y gestionar tickets cerrados.
 * El contenido y la funcionalidad se construirán progresivamente.
 */
const ClosedTicketsModal = ({ open, onClose, boardId }) => {
  const dispatch = useDispatch();
  const { getTokenSilent } = useToken();

  // Estado para manejar los tickets, paginación, carga y errores
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 5, // Límite de 5 en 5 como solicitado
    total: 0,
    totalPages: 0,
  });

  // Estado para el modal de detalle del ticket
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Función para cargar los tickets cerrados paginados
  const loadClosedTickets = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const token = await getTokenSilent();
      const offset = (page - 1) * pagination.limit;
      
      const result = await dispatch(getClosedTicketsByBoard(
        boardId,
        token,
        {}, // Sin filtros adicionales
        { offset, limit: pagination.limit }
      ));
      
      setTickets(result.tickets);
      setPagination(prev => ({
        ...prev,
        page,
        total: result.total,
        totalPages: Math.ceil(result.total / pagination.limit)
      }));
      
    } catch (err) {
      console.error('Error al cargar tickets cerrados:', err);
      setError(err.message || 'Error al cargar los tickets cerrados');
    } finally {
      setLoading(false);
    }
  }, [getTokenSilent, dispatch, boardId, pagination.limit]);

  // Cargar tickets cuando se abre el modal o cambia la página
  useEffect(() => {
    if (open) {
      loadClosedTickets(pagination.page);
    }
  }, [open, pagination.page, loadClosedTickets]);

  // Manejador para el cambio de página
  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleReopenSuccess = async (reopenedTicketId) => {
    // 1. Cerrar el modal de detalles
    setSelectedTicket(null);
    // 2. Cerrar el modal principal de tickets cerrados
    onClose();

    // 3. Forzar la recarga del tablero principal para que aparezca el ticket
    try {
        await dispatch(clearDateFilters(getTokenSilent));
    } catch (error) {
        console.error("Error al refrescar el tablero Kanban:", error);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Tickets Cerrados</Typography>
            <IconButton onClick={onClose} aria-label="Cerrar modal">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ p: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Cargando tickets...</Typography>
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Ticket</TableCell>
                      <TableCell>Fecha de Cierre</TableCell>
                      <TableCell>Contacto</TableCell>
                      <TableCell>Agente</TableCell>
                      <TableCell>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tickets.length > 0 ? (
                      tickets.map((ticket) => (
                        <TableRow
                          key={ticket.id}
                          sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                        >
                          <TableCell>
                            <Typography variant="subtitle2" fontWeight={500}>{ticket.name}</Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {ticket.description}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {new Date(ticket.updated_at).toLocaleString('es-ES')}
                          </TableCell>
                          <TableCell>{ticket.name_contact || ticket.contact_name || 'N/A'}</TableCell>
                          <TableCell>{ticket.assigned_agent_name || 'Sin asignar'}</TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Visibility />}
                              onClick={() => setSelectedTicket(ticket)}
                            >
                              Ver Detalles
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography>No se encontraron tickets cerrados.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={pagination.totalPages}
                    page={pagination.page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Modal de detalle del ticket */}
      {selectedTicket && (
        <TicketDetailModal
          open={!!selectedTicket}
          onClose={() => setSelectedTicket(null)}
          ticket={selectedTicket}
          // Pasaremos la columna como null ya que no aplica en esta vista
          column={null} 
          onReopenSuccess={handleReopenSuccess}
        />
      )}
    </>
  );
};

export default ClosedTicketsModal; 